import {
  View,
  type LayoutChangeEvent,
  type PointerEvent,
  type ViewStyle,
} from 'react-native'
import { useRef } from 'react'
import SteppedTrackVisual from './SteppedTrackVisual'
import {
  getSteppedValueFromRatio,
  getTrackFillPercent,
} from './steppedTrackUtils'
import {
  sliderConfig,
  sliderThumbOffset,
  sliderThumbStyle,
  sliderTrackContainerStyle,
  sliderTrackStyle,
} from './styles'
import type { SliderTrackProps } from './types'

const roundValue = (
  raw: number,
  step: number,
  minimumValue: number,
  maximumValue: number
) => {
  if (raw === minimumValue || raw === maximumValue) {
    return raw
  }

  const rounded =
    step > 0
      ? minimumValue + Math.round((raw - minimumValue) / step) * step
      : raw

  return Math.max(minimumValue, Math.min(rounded, maximumValue))
}

const sliderTrackStyleWeb: ViewStyle = {
  ...sliderTrackContainerStyle,
  justifyContent: 'center',
  ...( { touchAction: 'none', cursor: 'pointer' } as ViewStyle),
}

export default function SliderTrack({
  variant = 'default',
  minimumValue,
  maximumValue,
  value,
  step,
  steps,
  disabled,
  onValueChange,
  colorTrackFront,
  colorTrackBack,
}: SliderTrackProps) {
  const trackMetricsRef = useRef({ left: 0, width: 0 })
  const trackRef = useRef<View>(null)
  const isDraggingRef = useRef(false)
  const isStepped = variant === 'stepped'
  const steppedParams = { minimumValue, maximumValue, step, steps }

  const measureTrack = () => {
    trackRef.current?.measureInWindow((left, _top, width) => {
      trackMetricsRef.current = { left, width }
    })
  }

  const valueFromClientX = (clientX: number) => {
    const { left, width } = trackMetricsRef.current
    if (width <= 0) {
      return value
    }

    const ratio = Math.max(0, Math.min(1, (clientX - left) / width))

    if (isStepped) {
      return getSteppedValueFromRatio(ratio, steppedParams)
    }

    const raw = minimumValue + ratio * (maximumValue - minimumValue)
    return roundValue(raw, step, minimumValue, maximumValue)
  }

  const updateValue = (clientX: number) => {
    if (disabled) {
      return
    }

    const nextValue = valueFromClientX(clientX)
    if (nextValue === value) {
      return
    }

    onValueChange(nextValue)
  }

  const handleLayout = (_event: LayoutChangeEvent) => {
    measureTrack()
  }

  const getClientX = (nativeEvent: PointerEvent['nativeEvent']) => {
    return nativeEvent.clientX ?? nativeEvent.pageX ?? 0
  }

  const handlePointerDown = (event: PointerEvent) => {
    if (disabled) {
      return
    }

    isDraggingRef.current = true
    measureTrack()
    updateValue(getClientX(event.nativeEvent))

    const pointerId = (event.nativeEvent as { pointerId?: number }).pointerId
    const target = event.target as { setPointerCapture?: (id: number) => void } | null
    if (pointerId != null) {
      target?.setPointerCapture?.(pointerId)
    }
  }

  const handlePointerMove = (event: PointerEvent) => {
    if (!isDraggingRef.current || disabled) {
      return
    }

    updateValue(getClientX(event.nativeEvent))
  }

  const handlePointerUp = () => {
    isDraggingRef.current = false
  }

  const fillPercent = getTrackFillPercent(value, minimumValue, maximumValue)

  return (
    <View
      ref={trackRef}
      onLayout={handleLayout}
      style={sliderTrackStyleWeb}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {isStepped ? (
        <SteppedTrackVisual
          minimumValue={minimumValue}
          maximumValue={maximumValue}
          value={value}
          step={step}
          steps={steps}
          colorTrackFront={colorTrackFront}
          colorTrackBack={colorTrackBack}
        />
      ) : (
        <View
          style={{
            height: sliderConfig.trackRowHeight,
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <View
            style={{
              ...sliderTrackStyle,
              width: '100%',
              backgroundColor: colorTrackBack,
              overflow: 'visible',
            }}
          >
            <View
              style={{
                ...sliderTrackStyle,
                width: `${fillPercent}%`,
                backgroundColor: colorTrackFront,
              }}
            />
          </View>
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: `${fillPercent}%`,
              marginLeft: -sliderThumbOffset,
              backgroundColor: colorTrackFront,
              ...sliderThumbStyle,
            }}
          />
        </View>
      )}
    </View>
  )
}
